import { Component, Mixins, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import '@gig/magic-ui-kit';
import '@gig/magic-pages';
import '@gig/magic-responsible-gaming-prompters';
import '@gig/magic-corex';
import { magic } from '@gig/magic-sdk';
import { notificationService } from '@gig/magic-messaging';
import type { IMessageEmit } from '@gig/magic-messaging';
import type { IDepositDataEvent } from '@gig/magic-sdk/dist/events';
import { SessionCode } from '@gig/magic-sdk/dist/events';
import {
    CssDevice,
    FeatureEnabledV2,
    getFeatureValueV2,
    getTrackingService,
    ipCountryQuery,
    languageQuery,
    MagicConfigMixin,
    nativeAppService,
    queryTranslations,
    SessionChange,
} from '@gig/magic-common';
import '@gig/magic-gamification/dist/components/loyalty-progress';

import { MainNavigation } from '../main-navigation';
import { TopNavigation } from '../top-navigation';
import { BottomNavigation } from '../bottom-navigation';
import { AppFooter } from '../footer';
import { LiveChat } from '../live-chat';
import { ModalContainer } from '../modal-container';
import { RegilyManager } from '../regily-manager';
import { SourceOfWealthManager } from '../source-of-wealth-manager';
import { GameFrame } from '../game-frame';
import { ContinuePlaying } from '../continue-playing';
import { BlockManager } from '../block-manager';
import { ZendeskLoader } from '../zendesk-loader';
import { NotFound } from '../not-found';

import type { OmitFirst } from '../../types';
import {
    config,
    intercom,
    liveChat,
    smarticoService,
    zendeskChat,
    eyeAbleService,
} from '../../services';
import { RETURNING_USER_KEY } from '../../util';
import { setXtremePushUserId } from '../../util/xtremepush.util';

import { RouteName } from '../../routes/routes.constants';
import { prerender } from '../../services/seo/prerender.service';
import {
    actions as rgActions,
    RESPONSIBLE_GAMING_NAMESPACE,
} from '../../store/modules/responsible-gaming';
import {
    getters as nGetters,
    actions as nActions,
    NAVIGATION_NAMESPACE,
} from '../../store/modules/navigation';
import '../../styles/default.scss';
import { JurisdictionMixin } from '../../mixins/jurisdiction.mixin';
import { NotFoundMixin } from '../../mixins/notfound.mixin';
import {
    BREAKPOINT_DEVICE_UPDATED,
    BREAKPOINT_FULLSCREEN_UPDATED,
    DEVICE_QUERY_IS_IOS,
    DEVICE_QUERY_IS_IPAD,
    DEVICE_QUERY_IS_MOBILE,
    ENABLE_CONTINUE_PLAYING_UPDATED,
    Feature,
    FeatureSettings,
    GAMES_CURRENT_GAME_SLUG_UPDATED,
    GAMES_JACKPOTS_LIST,
    GAMES_LIST,
    IS_MOBILE_APP_QUERY,
    MESSAGING_MARK_READ,
    NET_POSITION_BAR_UPDATED,
    PANIC_BAR_VISIBLE,
    RECONNECTING_UPDATED,
} from '@gig/magic-messages';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { alternateLink } from '@gig/magic-frontend-toolkit/dist/services/alternate-link';
import { linktag } from '@gig/magic-frontend-toolkit/dist/services/linktag';
import { JACKPOTS_READY } from '../../routes/jackpots/jackpots';
import { IUserProfile } from '@gig/magic-sdk/dist/adapter/users/user';
import { NotificationDelay } from '@gig/magic-messaging';
import { TrackingService } from '@gig/magic-frontend-toolkit';

const rgModule = namespace(RESPONSIBLE_GAMING_NAMESPACE);
const navigationModule = namespace(NAVIGATION_NAMESPACE);

@Component({
    components: {
        MainNavigation,
        TopNavigation,
        BottomNavigation,
        AppFooter,
        LiveChat,
        ModalContainer,
        SourceOfWealthManager,
        GameFrame,
        ContinuePlaying,
        BlockManager,
        RegilyManager,
        ZendeskLoader,
        NotFound,
    },
})
export default class App extends Mixins(
    MagicConfigMixin,
    JurisdictionMixin,
    NotFoundMixin,
) {
    @FeatureEnabledV2({
        slug: Feature.COOKIE_POPUP,
    })
    cookiePopupEnabled = false;
    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })
    isTopNavigationEnabled = true;

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.EYE_ABLE_ENABLED,
    })
    isEyeAbleEnabled = true;

    public isMobileApp: boolean = false;
    topNavigationSettings: string = '';

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })

    country: Country | null = null;

    isLoggedIn = false;

    reconnecting: boolean = false;

    @rgModule.Action('clearLimits')
    clearLimits!: OmitFirst<typeof rgActions.clearLimits>;

    /**
     * Emits the action to toggle the sub top nav.
     */
    @navigationModule.Action('toggleSubTopNavHidden')
    toggleSubTopNavAction!: OmitFirst<typeof nActions.toggleSubTopNavHidden>;

    /**
     * Whether the main navigation bar is open or closed.
     * @type {ReturnType<typeof getters.isTopNavHidden>}
     */
    @navigationModule.Getter('isSubTopNavHidden')
    isSubTopNavHidden!: ReturnType<typeof nGetters.isSubTopNavHidden>;

    showNetPositionBar = false;
    enableContinuePlaying = false;
    isMobile = false;
    isFullscreen: boolean = false;
    shouldShowPWAInstall: boolean = false;
    currentGameSlug: string | null = null;
    panicBarVisible = false;
    isWalletOpen: boolean = false;

    private subscriptions: Array<() => void> = [];

    get kcContext() {
        return window.kcContext;
    }

    get isSweepstakes() {
        return config.sweepstakes;
    }

    get isMobileGamePlayView() {
        return !!this.currentGameSlug && this.isMobile;
    }

    get shouldDisableRGAlerts() {
        return this.$route.matched.some((route) => {
            return route.meta.shouldDisableRGAlerts === true;
        });
    }

    get shouldHideMainMenu() {
        const isLandingPage = this.$route.name === RouteName.LANDING;

        const hasHideMainMenuMeta = this.$route.matched.some((route) => {
            return route.meta.shouldHideMainMenu === true;
        });

        return hasHideMainMenuMeta || (!this.isMobile && isLandingPage);
    }

    get isNotFoundRoute(): boolean {
        return this.isNotFound;
    }

    get isSportsbookPage(): boolean {
        return this.$route.name === RouteName.SPORTSBOOK;
    }

    get homepageUrl() {
        return window.location.origin;
    }

    get isTopNavWithBottomEnabled() {
        return (
            this.isTopNavigationEnabled &&
            this.topNavigationSettings.includes(
                FeatureSettings.BOTTOM_NAVIGATION_ENABLED,
            )
        );
    }

    mounted() {
        const attributes = config.cookieBot;
        if (!attributes?.src) {
            return;
        }

        const cookieBot = document.createElement('script');
        for (const [key, value] of Object.entries(attributes)) {
            cookieBot.setAttribute(key, value);
        }
        document.head.appendChild(cookieBot);
    }

    async created() {
        this.isMobileApp =
            (await window.queryBus.query(IS_MOBILE_APP_QUERY)) ?? false;
        this.country = await ipCountryQuery();
        if (this.kcContext) {
            this.handleEmailVerificationNotification();
        }

        if (!window.inTestEnvironment) {
            prerender.waitFor(this.requestGames(), this.requestJackpots(), this.waitForJackpotsIFrame());
        }

        this.topNavigationSettings = await getFeatureValueV2(
            Feature.TOP_NAVIGATION,
            '',
        );

        this.setDefaultMetaTags();
        linktag.setLanguagesConfig(config.languages);

        this.topNavigationSettings = await getFeatureValueV2(
            Feature.TOP_NAVIGATION,
            '',
        );

        if (magic.session.isCreated()) {
            this.handleSessionCreated();
            await this.handleFirstLogin();
        }
        magic.events.sessionDestroyed(() => this.handleSessionDestroyed());
        magic.events.sessionCreated(() => this.handleSessionCreated());
        magic.events.sessionChange(this.handleSessionChanged);
        magic.events.firstDeposit(this.handleFirstDeposit);
        magic.events.subsequentDeposit(this.handleSubsequentDeposit);
        magic.events.sessionDestroyedCoreX(() =>
            this.handleCorexSessionDestroyed(),
        );

        liveChat?.updateLiveChat(magic.session.getUser());
        intercom?.load(magic.session.getUser());

        const language = await languageQuery();

        smarticoService.setUserData(magic.session.getUser(), language);

        if (this.isEyeAbleEnabled && config.eyeAble?.apiKey?.trim()) {
            try {
                await eyeAbleService.load();
            } catch (error) {
                console.error('[App] Eye-Able initialization failed:', error);
            }
        }

        this.checkIsWalletOpen();

        this.getPWAstatus();

        window.eventBus.subscribe<boolean>(
            RECONNECTING_UPDATED,
            (e) => {
                this.reconnecting = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            NET_POSITION_BAR_UPDATED,
            (event) => {
                this.showNetPositionBar = event.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            ENABLE_CONTINUE_PLAYING_UPDATED,
            (event) => {
                this.enableContinuePlaying = event.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<CssDevice>(
            BREAKPOINT_DEVICE_UPDATED,
            async () => {
                this.isMobile = await window.queryBus.query(
                    DEVICE_QUERY_IS_MOBILE,
                )!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            BREAKPOINT_FULLSCREEN_UPDATED,
            (e) => {
                this.isFullscreen = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<string | null>(
            GAMES_CURRENT_GAME_SLUG_UPDATED,
            (e) => {
                this.currentGameSlug = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            PANIC_BAR_VISIBLE,
            async (e) => {
                this.panicBarVisible = e.payload!;
            },
            { replay: true },
        );

        TrackingService.setupUserTracking();
    }

    @Watch('magicConfig.language')
    async languageChange() {
        if (config?.zendesk?.version === 'legacy') {
            zendeskChat.setZendeskSettings();
        }

        const user = magic.session.getUser();
        const newLanguage = await languageQuery();
        smarticoService.setUserData(user, newLanguage);
    }

    private requestJackpots(): Promise<void> {
        return new Promise((resolve) => {
            const subscription = window.eventBus.subscribe(
                GAMES_JACKPOTS_LIST,
                () => {
                    resolve();
                    subscription.unsubscribe();
                },
                { replay: true },
            );
        });
    }

    private waitForJackpotsIFrame(): Promise<void> {
        return new Promise((resolve) => {
            if(this.$route.name !== RouteName.JACKPOTS){
                resolve();
                return;
            }

            const subscription = window.eventBus.subscribe(JACKPOTS_READY, () => {
                resolve();
                subscription.unsubscribe();
            },
            { replay: true }
            )
        })
    }

    @Watch('$route.path')
    onRouteChange() {
        this.setDefaultMetaTags();
        this.resetNotFound();
    }

    get isTopNavigationLight() {
        return this.$route.matched.reduce(
            (prev, cur) => ({ ...prev, ...cur.meta.topNavigation }),
            {
                isLight: false,
            },
        ).isLight;
    }

    private setDefaultMetaTags() {
        queryTranslations().then(() => {
            linktag.setCanonicalTagFromPath(this.$route.path);
            alternateLink.setAlternateTag(this.$route.path, {
                useBaseUrl: true,
            });
        });
    }

    private handleEmailVerificationNotification() {
        const url = new URL(window.location.href);
        const verificationStatus = url.searchParams.get('accountVerified');

        if (verificationStatus) {
            notificationService.success(
                this.$t('sign-up.corex.verification-success'),
                { closeDelay: NotificationDelay.PERMANENT },
            );

            url.searchParams.delete('accountVerified');
            window.history.replaceState(null, '', url.toString());
        }
    }

    @Watch('$route.query')
    checkIsWalletOpen() {
        const queryModalPath = this.$route.query.modal;
        this.isWalletOpen =
            (Array.isArray(queryModalPath) &&
                queryModalPath.some((path) =>
                    path?.includes(RouteName.WALLET),
                )) ||
            !!queryModalPath?.includes(RouteName.WALLET);
        this.resetNotFound();
    }

    @SessionChange({ immediate: true })
    async onSessionChange(isUserLoggedIn: boolean) {
        this.isLoggedIn = isUserLoggedIn;
    }

    onCookiePopupResponse(accepted: boolean) {
        getTrackingService().trackCookieAcceptance(accepted);
    }

    toggleSubTopNav(event: Event) {
        event.preventDefault();
        this.toggleSubTopNavAction();
    }

    markMessageAsRead(payload: IMessageEmit) {
        window.commandBus.publish(MESSAGING_MARK_READ, payload);
    }

    async getPWAstatus() {
        const [isIOS, isIpad] = await Promise.all([
            window.queryBus.query<boolean>(DEVICE_QUERY_IS_IOS),
            window.queryBus.query<boolean>(DEVICE_QUERY_IS_IPAD),
        ]);
        this.shouldShowPWAInstall = isIOS! || isIpad!;
    }

    private requestGames(): Promise<void> {
        return new Promise((resolve) => {
            const subscription = window.eventBus.subscribe(
                GAMES_LIST,
                () => {
                    resolve();
                    subscription.unsubscribe();
                },
                { replay: true },
            );
        });
    }

    private async handleSessionDestroyed() {
        liveChat?.updateLiveChat(null);
        intercom?.shutdown();
        this.clearLimits();

        smarticoService.setUserData(null, null);

        this.subscriptions.forEach((unsub) => unsub());
        this.subscriptions = [];

        return this.checkRouteRestriction();
    }

    private async handleCorexSessionDestroyed() {
        intercom?.shutdown();
    }

    private handleSessionChanged(code: SessionCode) {
        if (
            [
                SessionCode.EXPIRED,
                SessionCode.TERMINATED_BY_ANOTHER_LOGIN,
                SessionCode.TERMINATED_BY_PRESET_TIME,
                SessionCode.TERMINATED_BY_SELF_EXCLUSION,
                SessionCode.TERMINATED_BY_GEO_LOCATION,
            ].includes(code)
        ) {
            nativeAppService.postSystemLogout();
        }

        if (code === SessionCode.TERMINATED_BY_GEO_LOCATION) {
            notificationService.error(this.$t('session.logout.geo-location'));
        }

        if (SessionCode.LOGGED_OFF) {
            nativeAppService.postLogout();
        }
    }

    private handleFirstDeposit(event: IDepositDataEvent) {
        const { amount, currency } = event;
        nativeAppService.trackFirstDeposit(amount, currency);
    }

    private handleSubsequentDeposit(event: IDepositDataEvent) {
        const { amount, currency } = event;
        nativeAppService.trackDeposit(amount, currency);
    }

    private async handleFirstLogin() {
        const user = (await magic.invokeCached(
            60,
            magic.methods.users,
            'getUserProfile',
        )()) as IUserProfile;

        if (user && user.lastLogins.length === 1) {
            getTrackingService().trackFirstLogin();
        }
    }

    private async checkRouteRestriction() {
        // if we attempt to open the login modal, we will redirect the user to the keycloak page
        // instead, we just redirect to the homepage
        return this.$router.push({
            name: RouteName.HOME,
        });
    }

    private async handleSessionCreated() {
        const user = magic.session.getUser();
        
        try {
            await setXtremePushUserId(user?.userId || '');
        } catch (e) {
            console.warn('XtremePush failed to set user ID:', e);
        }
        const language = await languageQuery();

        liveChat?.updateLiveChat(user);
        intercom?.update(user);
        smarticoService.setUserData(magic.session.getUser(), language);

        this.subscriptions.push(
            magic.events.sowqRequested(() => {
                if (this.$route.name !== RouteName.SOW) {
                    this.$router.push({
                        name: RouteName.SOW,
                    });
                }
            }).unsub,
        );

        try {
            window.localStorage.setItem(RETURNING_USER_KEY, '1');
        } catch (e) {
            console.error(e);
        }
    }
}
